import { NgTemplateOutlet } from '@angular/common'
import {
    Component,
    OnInit,
    TemplateRef,
    computed,
    effect,
    inject,
    input,
    numberAttribute,
    viewChild,
} from '@angular/core'
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { NgbModal, NgbNavModule } from '@ng-bootstrap/ng-bootstrap'
import { distinctUntilChanged, map } from 'rxjs'
import { ButtonComponent } from 'src/app/shared/components/form-elements/button/button.component'
import { SmartQuoteLineItem } from 'src/app/shared/interfaces/smartquote/smart-quote-line.interface'
import { SmartQuote } from 'src/app/shared/interfaces/smartquote/smart-quote.interface'
import { SmartquoteHelperService } from '../services/smartquote-helper.service'
import { ToastrService } from 'ngx-toastr'
import { CanDeactivateComponent } from 'src/app/core/guards/unsaved-changes.guard'
import { AppStore } from 'src/app/core/app.store'
import { WolseleyService } from 'src/app/core/services/wolseley/wolseley.service'
import { SmartQuoteLineForm } from '../helpers/smartquote-line-form.interface'
import { SmartQuoteForm } from '../helpers/smartquote-form.interface'
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { toSignal } from '@angular/core/rxjs-interop'
import { environment } from 'src/environments/environment'
import { PageActionBarComponent } from 'src/app/shared/components/page-action-bar/page-action-bar.component'
import { ConditionalRequiredValidator } from 'src/app/shared/validators/conditionalRequired.validator'

import { SmartquoteDetailsComponent } from './components/smartquote-details/smartquote-details.component'
import { SmartquoteTopBar2Component } from './components/smartquote-top-bar-2/smartquote-top-bar-2.component'
import { PagePresenceIndicatorComponent } from 'src/app/shared/components/page-presence/page-presence-indicator.component'

@Component({
    selector: 'sf-smartquote',
    imports: [
        NgbNavModule,
        NgxPermissionsModule,
        FontAwesomeModule,
        ButtonComponent,
        PageActionBarComponent,
        ButtonComponent,
        SmartquoteDetailsComponent,
        SmartquoteTopBar2Component,
        PagePresenceIndicatorComponent,
    ],
    templateUrl: './smartquote-edit.component.html',
    styleUrls: ['./smartquote-edit.component.scss'],
    providers: [SmartquoteHelperService],
})
export class SmartquoteEditComponent extends CanDeactivateComponent implements OnInit {
    #fb = inject(FormBuilder)
    #modalService = inject(NgbModal)
    #ngxPermissionsService = inject(NgxPermissionsService)
    #route = inject(ActivatedRoute)
    #smartquoteHelperService = inject(SmartquoteHelperService)
    #toastr = inject(ToastrService)
    #wolseleyService = inject(WolseleyService)

    smartQuoteTemplates = this.#smartquoteHelperService.smartQuoteTemplates
    companySettings = this.#smartquoteHelperService.companySettings
    customer = this.#smartquoteHelperService.customer
    mainAppliance = this.#smartquoteHelperService.mainAppliance
    appointment = this.#smartquoteHelperService.appointment
    existingSmartQuote = this.#smartquoteHelperService.existingSmartQuote
    quoteAccepted = computed(() => this.#smartquoteHelperService.existingSmartQuote()?.accepted || false)
    quoteDeclined = computed(() => this.#smartquoteHelperService.existingSmartQuote()?.declined || false)
    orderPlaced = computed(() => this.#smartquoteHelperService.existingSmartQuote()?.hasOrderBeenPlaced || false)
    workAddress = computed(() => {
        const quote = this.existingSmartQuote()
        const items = [quote?.addressLine1, quote?.addressLine2, quote?.town, quote?.county, quote?.postcode]
        return items.filter(Boolean).join(', ')
    })
    orders = this.#smartquoteHelperService.orders
    statistics = this.#smartquoteHelperService.statistics
    landingPageUrl = computed(() => {
        const smartQuote = this.existingSmartQuote()
        const customer = this.customer()
        const companySettings = this.#smartquoteHelperService.companySettings()

        if (smartQuote === null || customer === null || companySettings === null) {
            return null
        }

        return `${environment.legacyUrl}public/smartquoteaccept/${companySettings.companyId}/${smartQuote.id}/${customer.apiKey}`
    })

    awaitingorderModal = viewChild<TemplateRef<any>>('awaitingorderModal')

    #appStore = inject(AppStore)

    markup = computed(() => {
        const markup = this.#appStore.companySettings()?.smartQuotePartsMarkup
        return markup ? markup / 100 + 1 : 1
    })

    form: FormGroup = this.#fb.group<SmartQuoteForm>(
        {
            customerId: this.#fb.control(null, [Validators.required]),
            emailAddress: this.#fb.control(null, [Validators.email]),
            customerName: this.#fb.nonNullable.control('', [Validators.required]),
            addressLine1: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
            addressLine2: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
            town: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
            county: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
            postcode: this.#fb.nonNullable.control<string>('', [Validators.maxLength(255)]),
            workAddress: this.#fb.nonNullable.control('', [Validators.required, Validators.maxLength(1000)]),
            notes: this.#fb.nonNullable.control('', [Validators.maxLength(2000)]),
            engineerNotes: this.#fb.nonNullable.control('', [Validators.maxLength(2000)]),
            staffNotes: this.#fb.nonNullable.control('', [Validators.maxLength(2000)]),
            labourRate: this.#fb.nonNullable.control(0),
            labourCost: this.#fb.nonNullable.control({ value: 0, disabled: true }),
            labourHours: this.#fb.nonNullable.control(0),
            partsCost: this.#fb.nonNullable.control({ value: 0, disabled: true }),
            deposit: this.#fb.nonNullable.control({ value: 0, disabled: true }),
            depositPercentage: this.#fb.nonNullable.control(0),
            discount: this.#fb.nonNullable.control({ value: 0, disabled: true }),
            discountPercentage: this.#fb.nonNullable.control(0),
            moneyOff: this.#fb.nonNullable.control(0),
            vatMultiplier: this.#fb.nonNullable.control(1.2, [Validators.required]),
            vatTotal: this.#fb.nonNullable.control({ value: 0, disabled: true }),
            combinedLines: this.#fb.nonNullable.control(false),
            freeOfCharge: this.#fb.nonNullable.control(false),
            defaultImageUrl: this.#fb.control(null, [Validators.maxLength(500)]),
            notifyUsers: this.#fb.nonNullable.control<boolean>(true),
            lineItems: this.#fb.array<SmartQuoteLineForm>([]),
        },
        {
            validators: [
                ConditionalRequiredValidator('workAddress', '', 'addressLine1', true),
                ConditionalRequiredValidator('workAddress', '', 'postcode', true),
                ConditionalRequiredValidator('workAddress', '', 'workAddress', false),
            ],
        }
    )

    get lineItems(): FormArray<SmartQuoteLineForm> {
        return this.form.get('lineItems') as FormArray
    }

    get doAllLineItemsHaveCatalogNumbers(): boolean {
        const result =
            !!this.lineItems.length &&
            this.lineItems.value.every((x) => x.catalogNumber !== null && x.catalogNumber !== undefined)
        return result
    }

    catalogCodes = toSignal(
        this.form.valueChanges.pipe(
            map((x) => x.lineItems),
            map((x) =>
                x
                    .map((l: SmartQuoteLineItem) => ({ productCode: l.catalogNumber, quantity: l.quantity }))
                    .filter(
                        (p: { productCode: null | undefined }) => p.productCode !== null && p.productCode !== undefined
                    )
            )
        )
    )

    customerId = input(null, { transform: numberAttribute })
    smartquoteId = input(null, { transform: numberAttribute })

    constructor() {
        super()

        effect(() => {
            const companySettings = this.#smartquoteHelperService.companySettings()

            if (companySettings && !this.existingSmartQuote()) {
                this.form.patchValue({
                    combinedLines: companySettings.smartQuoteCombinedLines,
                    labourRate: companySettings.engineerCostPerHour,
                })
            }
        })
    }

    smartQuoteEffect = effect(() => {
        const smartquote = this.existingSmartQuote()
        if (smartquote === null) {
            this.form.reset()
            return
        }

        if (smartquote.accepted && !smartquote.hasOrderBeenPlaced) {
            this.#modalService.open(this.awaitingorderModal(), { centered: true, backdrop: 'static' })
        }

        this.form.patchValue(smartquote)
        smartquote.lineItems.forEach((lineItem) => {
            this.lineItems.push(this.mapLineItem(lineItem))
        })
    })

    customerEffect = effect(() => {
        const customer = this.customer()

        if (!customer || this.existingSmartQuote()) {
            return
        }

        this.form.patchValue({
            customerId: customer.id,
            customerName: customer.fullName,
            emailAddress: customer.emailAddress,
        })

        if (this.smartquoteId() && !customer.postcode) {
            this.form.patchValue({
                workAddress: customer.fullAddressReturnCarriage,
            })
        } else {
            this.form.patchValue({
                addressLine1: customer.addressLine1,
                addressLine2: customer.addressLine2,
                town: customer.town,
                county: customer.county,
                postcode: customer.postcode,
            })
        }
    })

    ngOnInit(): void {
        this.#smartquoteHelperService.customerId.set(this.customerId())
        this.#smartquoteHelperService.smartQuoteId.set(this.smartquoteId())

        this.form.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
            this.#smartquoteHelperService.updateStats(value as SmartQuote)
            const statistics = this.#smartquoteHelperService.statistics()

            const controlsToUpdate: Record<string, number> = {
                deposit: statistics.depositTotal,
                labourCost: statistics.labourCost,
                partsCost: statistics.linesTotal,
                discount: statistics.discountAsPercentage,
                vatTotal: statistics.totalVat,
            }

            Object.keys(controlsToUpdate).forEach((controlName) => {
                this.form.controls[controlName].patchValue(controlsToUpdate[controlName], {
                    emitEvent: false,
                    onlySelf: true,
                })
            })
        })
    }

    mapLineItem(item: SmartQuoteLineItem): FormGroup {
        return this.#fb.group({
            id: [item.id],
            catalogNumber: [item.catalogNumber],
            productId: [item.productId],
            description: [item.description],
            imageUrl: [item.imageUrl],
            mpn: [item.mpn],
            quantity: [item.quantity, [Validators.min(1)]],
            cost: [item.cost, [Validators.required]],
            terms: [item.terms],
        })
    }

    saveSmartQuote() {
        this.form.markAsDirty()
        this.form.markAsTouched()

        if (this.form.invalid) {
            this.#toastr.error('Please fill in all required fields')
            return
        }

        this.form.markAsPristine()
        this.form.markAsUntouched()

        const smartQuote = this.form.getRawValue() as SmartQuote

        if (!this.existingSmartQuote()) {
            this.#smartquoteHelperService.createSmartquote(smartQuote)
        } else {
            this.#smartquoteHelperService.updateSmartquote(smartQuote)
        }
    }

    acceptQuote(): void {
        this.form.markAsDirty()
        this.form.markAsTouched()

        if (this.form.invalid) {
            this.#toastr.error('Please fill in all required fields')
            return
        }

        this.#smartquoteHelperService.markQuoteAsAccepted()
    }

    declineQuote(): void {
        this.form.markAsDirty()
        this.form.markAsTouched()

        if (this.form.invalid) {
            this.#toastr.error('Please fill in all required fields')
            return
        }

        this.#smartquoteHelperService.markQuoteAsDeclined()
    }

    deleteQuote(): void {
        this.#smartquoteHelperService.deleteSmartQuote()
    }

    copyQuote(): void {
        this.#smartquoteHelperService.copySmartquote()
    }

    async loadWolseleyTerms(): Promise<void> {
        const hasWolseley = await this.#ngxPermissionsService.hasPermission('Wolseley')

        if (!hasWolseley) {
            return
        }

        const productCodes = this.lineItems.value
            ?.filter(
                (x) => x.catalogNumber !== null && x.catalogNumber !== undefined && (x.terms === 0 || x.terms === null)
            )
            .map((x) => x.catalogNumber!)

        this.#wolseleyService.getWolseleyPrices(productCodes).subscribe((prices) => {
            this.lineItems.controls.forEach((control) => {
                const lineItem = control.value
                const price = prices.products.productPrices.find((x) => x.productCode === lineItem.catalogNumber)

                if (price) {
                    control.patchValue({ terms: price.sellingPrice, productId: price.productId }, { emitEvent: true })
                }
            })
        })
    }

    setDefaultImageUrl(imageUrl: string): void {
        this.form.patchValue({ defaultImageUrl: imageUrl })
    }

    public override canDeactivate(): boolean {
        return !(this.form.dirty && this.form.touched)
    }
}
